import { Left, Right, Favorite, Home, Search, Settings } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import {
  PendingChangesBadge,
  ShortcutBox,
  UserAvatar,
  VBox,
  shortcuts,
  useDeviceState,
  useFavorites,
  useHover,
  useNavigate,
  Typography,
  useTunaMediaQuery,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import { useCallback, useEffect, useRef, type FC } from 'react'
import styles from './app-sidebar.module.css'
import { ToolsMenuItems } from './tools-menu-items.js'

export interface AppSideBarProps {
  entity?: Persisted<Entity>
}

export const AppSideBar: FC<AppSideBarProps> = props => {
  const { entity } = props

  const { favorites } = useFavorites()

  const { userId } = useCurrentUser()

  const isSmall = useTunaMediaQuery('(max-width: 600px)')
  const avatarSize = isSmall ? 'tiny' : 'small'

  const { dialog: searchDialog, setOpen: setSearchOpen, open: searchOpen } = useSearchDialog(entity)

  const navigate = useNavigate()

  const ref = useRef<HTMLDivElement>(null)
  const hover = useHover(ref.current)

  const [collapsed, setCollapsed, collapsedReady] = useDeviceState('sidebar.collapsed', false)
  const [width, setWidth, widthReady] = useDeviceState('sidebar.width', 192)
  const sidebarReady = collapsedReady && widthReady
  const cleanupRef = useRef<(() => void) | null>(null)

  // Clean up resize listeners on unmount
  useEffect(() => () => cleanupRef.current?.(), [])

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width ?? 192
    let currentWidth = startWidth

    const handleMouseMove = (e: MouseEvent) => {
      currentWidth = Math.min(400, Math.max(120, startWidth + (e.clientX - startX)))
      setWidth(currentWidth)
    }

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      cleanupRef.current = null
    }

    const handleMouseUp = () => cleanup()

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    cleanupRef.current = cleanup
  }, [width, setWidth])

  const handleToggleCollapse = () => setCollapsed(!collapsed)

  return (
    <VBox
      ref={ref}
      className={cn(styles.sidebar, collapsed && styles['sidebar--collapsed'], !sidebarReady && styles['sidebar--no-transition'])}
      style={!collapsed ? { width } : undefined}
    >
      {!collapsed ? (
        <div className={styles.userRow}>
          <UserAvatar size={avatarSize} userId={userId} aria-label="user" showName={true} />
          <PendingChangesBadge />
        </div>
      ) : null}

      <div className={styles.nav}>
        <button
          onClick={() => navigate('/app')}
          className={cn(styles.menuItem, location.pathname === '/app' && styles['menuItem--active'])}
          aria-current={location.pathname === '/app' ? 'page' : undefined}
          title="Home"
        >
          <Home />
          {!collapsed ? <span>Home</span> : null}
        </button>
        <button
          onClick={() => navigate('/app/automations')}
          className={cn(styles.menuItem, location.pathname === '/app/automations' && styles['menuItem--active'])}
          aria-current={location.pathname === '/app/automations' ? 'page' : undefined}
          title="Automations"
        >
          <Settings />
          {!collapsed ? <span>Automations</span> : null}
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          className={cn(styles.menuItem, searchOpen && styles['menuItem--active'])}
          title="Search"
        >
          <Search />
          {!collapsed ? (
            <>
              <span className={styles.spacer}>Search</span>
              <ShortcutBox shortcut={shortcuts.Search} showDescription={false} />
            </>
          ) : null}
        </button>

        {!collapsed ? (
          <>
            <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
              <Typography variant="caption">Tools</Typography>
            </div>

            <ToolsMenuItems variant="caption" />

            <div className={styles.spacer} />

            <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
              <Favorite size={16} />
              <Typography variant="caption">Favorites</Typography>
            </div>

            {favorites.map(favorite => (
              <button key={favorite.path} className={styles.menuItem} onClick={() => navigate(favorite.path)}>
                <span style={{ color: hover ? favorite.color : 'var(--tuna-palette-action-disabled)' }}>
                  <favorite.Icon />
                </span>
                <Typography variant="caption">{favorite.name}</Typography>
              </button>
            ))}
            {favorites.length === 0 ? (
              <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
                <Typography variant="caption">No favorites</Typography>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <button
        className={styles.collapseButton}
        onClick={handleToggleCollapse}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <Right size={16} /> : <Left size={16} />}
      </button>

      {!collapsed ? (
        <div className={styles.resizeHandle} onMouseDown={handleResizeStart} />
      ) : null}

      {searchDialog}
    </VBox>
  )
}
